/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Tooltip } from 'antd'
import {
  ColumnHeightOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  VerticalAlignMiddleOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  paragraphSmall,
  paragraphXSmall,
  weightSemiBold,
} from 'src/components/css'

const translate = t as (key: string, options?: unknown) => string

interface TreeSearchBarProps {
  term: string
  onTermChange: (term: string) => void
  matchCount: number
  isSearching: boolean
  /** True when no node is expanded, so the control offers Expand All. */
  isAllCollapsed: boolean
  onToggleAll: () => void
  isFullscreen: boolean
  onToggleFullscreen: () => void
}

/**
 * Search over the whole hierarchy. Filtering happens in place in the tree
 * below, so there is no result list here — only the term and how many nodes
 * matched.
 */
const TreeSearchBar = ({
  term,
  onTermChange,
  matchCount,
  isSearching,
  isAllCollapsed,
  onToggleAll,
  isFullscreen,
  onToggleFullscreen,
}: TreeSearchBarProps) => {
  'use memo'

  const toggleAllLabel = capitalizeWords(
    isAllCollapsed ? 'Expand all' : 'Collapse all'
  )
  const fullscreenLabel = capitalizeWords(
    isFullscreen ? 'Exit full screen' : 'Full screen'
  )

  return (
    <div css={wrapperStyle}>
      <div css={rowStyle}>
        <div css={fieldStyle}>
          <input
            css={inputStyle}
            type='search'
            value={term}
            placeholder={capitalizeWords(
              translate('Web:searchCompetitionPlaceholder')
            )}
            aria-label={capitalizeWords(
              translate('Web:searchCompetitionPlaceholder')
            )}
            onChange={(event) => onTermChange(event.target.value)}
          />
          {isSearching && (
            <button
              type='button'
              aria-label={translate('common:clear')}
              css={clearStyle}
              onClick={() => onTermChange('')}
            >
              ×
            </button>
          )}
        </div>

        {/*
         * Hidden while searching: search force-expands every surviving branch,
         * so the control would report a state the tree is not in and its click
         * would be undone on the next keystroke.
         */}
        {!isSearching && (
          <Tooltip title={toggleAllLabel} placement='topRight'>
            <button
              type='button'
              css={squareButtonStyle}
              aria-label={toggleAllLabel}
              onClick={onToggleAll}
            >
              {isAllCollapsed ? (
                <ColumnHeightOutlined />
              ) : (
                <VerticalAlignMiddleOutlined />
              )}
            </button>
          </Tooltip>
        )}

        {/*
         * Never hidden, unlike the toggle above: in full screen the shell
         * covers the nav and the tab bar, so this button and Escape are the
         * only ways out.
         */}
        <Tooltip title={fullscreenLabel} placement='topRight'>
          <button
            type='button'
            css={squareButtonStyle}
            aria-label={fullscreenLabel}
            aria-pressed={isFullscreen}
            onClick={onToggleFullscreen}
          >
            {isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
          </button>
        </Tooltip>
      </div>
      {isSearching && (
        <p css={matchLineStyle} role='status'>
          <strong>{matchCount}</strong>{' '}
          {matchCount === 1
            ? translate('common:match')
            : translate('common:match_plural')}
        </p>
      )}
    </div>
  )
}

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid ${colors.SECONDARY_300};
`

const rowStyle = css`
  display: flex;
  align-items: stretch;
  gap: 8px;
`

/*
 * A RAISED surface, unlike the search field's recessed one. Sharing the input's
 * background and border read as a disabled affix of the input rather than as
 * its own control. Shared by Expand/Collapse All and Full Screen.
 */
const squareButtonStyle = css`
  flex: none;
  width: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.SECONDARY_300};
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 6px;
  color: ${colors.NEUTRAL_0};
  font-size: 16px;
  cursor: pointer;
  padding: 0;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${colors.PRIMARY}1f;
    border-color: ${colors.PRIMARY};
    color: ${colors.PRIMARY};
  }

  &:active {
    background: ${colors.PRIMARY}33;
  }

  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: 2px;
  }
`

const fieldStyle = css`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${colors.SECONDARY_500};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 6px;
  padding: 8px 10px;

  &:focus-within {
    border-color: ${colors.PRIMARY};
  }
`

const inputStyle = css`
  ${paragraphSmall}
  flex: 1;
  min-width: 0;
  border: none;
  background: none;
  outline: none;
  color: ${colors.NEUTRAL_0};

  &::placeholder {
    color: ${colors.NEUTRAL_100};
  }
`

const clearStyle = css`
  border: none;
  background: none;
  color: ${colors.NEUTRAL_100};
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
`

const matchLineStyle = css`
  ${paragraphXSmall}
  margin: 0;
  padding: 0 2px;
  color: ${colors.NEUTRAL_100};
  font-variant-numeric: tabular-nums;

  strong {
    ${weightSemiBold}
    color: ${colors.PRIMARY};
  }
`

export default TreeSearchBar
